import { Layout } from '../components/Layout';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Receipt, IndianRupee, ShoppingBag, TrendingUp, Store, Wallet } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Invoice } from '../components/Invoice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const now = new Date();

export default function Sales({ user, onLogout }) {
  const [period, setPeriod] = useState('day'); // day | month | year
  const [day, setDay] = useState(now.toISOString().split('T')[0]);
  const [month, setMonth] = useState(now.toISOString().slice(0, 7));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [business, setBusiness] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);

  const anchor = period === 'day' ? day : period === 'month' ? month : year;

  useEffect(() => {
    axios.get(`${API}/settings/business`, auth()).then((r) => setBusiness(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/pos/period-summary?period=${period}&date=${anchor}`, auth()).then((r) => setSummary(r.data)).catch(() => {});
    if (period === 'day') {
      axios.get(`${API}/pos/orders?date=${day}`, auth()).then((r) => setOrders(r.data)).catch(() => {});
    }
  }, [period, anchor, day]);

  const label = (l) => {
    if (period === 'day') return `${l.slice(11, 13)}:00`;      // hour
    if (period === 'month') return l.slice(8, 10);              // day of month
    return MONTHS[parseInt(l.slice(5, 7), 10) - 1] || l;        // month name
  };
  const periodTitle = period === 'day' ? 'Hourly breakdown' : period === 'month' ? 'Daily breakdown' : 'Monthly breakdown';
  const maxSales = useMemo(() => Math.max(1, ...(summary?.breakdown || []).map((b) => b.sales)), [summary]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-4xl font-bold text-stone-900">Sales</h1>
            <p className="text-stone-600 mt-1">Daily, monthly & yearly sales</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-stone-100 rounded-lg p-1" data-testid="sales-period-toggle">
              {[['day', 'Day'], ['month', 'Month'], ['year', 'Year']].map(([val, lbl]) => (
                <button
                  key={val}
                  data-testid={`sales-period-${val}`}
                  onClick={() => setPeriod(val)}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${period === val ? 'bg-white text-primary shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
            {period === 'day' && <Input data-testid="sales-date" type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44" />}
            {period === 'month' && <Input data-testid="sales-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />}
            {period === 'year' && (
              <Input data-testid="sales-year" type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(e.target.value)} className="w-28" />
            )}
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-stone-200 rounded-lg p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center shrink-0"><IndianRupee className="text-secondary" /></div>
              <div><div className="text-sm text-stone-500">Total Sales</div><div className="text-2xl font-bold" data-testid="summary-sales">{fmt(summary.total_sales)}</div></div>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><ShoppingBag className="text-primary" /></div>
              <div><div className="text-sm text-stone-500">Orders</div><div className="text-2xl font-bold" data-testid="summary-orders">{summary.order_count}</div></div>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center shrink-0"><TrendingUp className="text-accent" /></div>
              <div><div className="text-sm text-stone-500">Avg Order</div><div className="text-2xl font-bold">{fmt(summary.avg_order)}</div></div>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center shrink-0"><Receipt className="text-stone-600" /></div>
              <div><div className="text-sm text-stone-500">GST Collected</div><div className="text-2xl font-bold">{fmt(summary.total_tax)}</div></div>
            </div>
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid="channel-split">
              <div className="flex items-center gap-2 mb-3"><Store size={18} className="text-primary" /><h3 className="font-semibold text-stone-800">Sales by Channel</h3></div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-stone-600">Counter / POS (offline)</span><span className="font-semibold" data-testid="channel-pos">{fmt(summary.pos_sales)} <span className="text-stone-400 text-xs">· {summary.pos_count}</span></span></div>
                <div className="flex justify-between"><span className="text-stone-600">Cake Orders (online)</span><span className="font-semibold" data-testid="channel-cake">{fmt(summary.cake_sales)} <span className="text-stone-400 text-xs">· {summary.cake_count}</span></span></div>
                <div className="flex h-2.5 rounded-full overflow-hidden mt-2 bg-stone-100">
                  <div className="bg-primary/70" style={{ width: `${summary.total_sales ? (summary.pos_sales / summary.total_sales) * 100 : 0}%` }} />
                  <div className="bg-secondary" style={{ width: `${summary.total_sales ? (summary.cake_sales / summary.total_sales) * 100 : 0}%` }} />
                </div>
              </div>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid="payment-split">
              <div className="flex items-center gap-2 mb-3"><Wallet size={18} className="text-accent" /><h3 className="font-semibold text-stone-800">Payments Collected</h3></div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-stone-600">Online (UPI / card / Razorpay)</span><span className="font-semibold" data-testid="pay-online">{fmt(summary.online_collected)}</span></div>
                <div className="flex justify-between"><span className="text-stone-600">Offline (cash)</span><span className="font-semibold" data-testid="pay-offline">{fmt(summary.offline_collected)}</span></div>
                {Object.keys(summary.by_payment_mode || {}).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-dashed border-stone-200 mt-2">
                    {Object.entries(summary.by_payment_mode).map(([m, amt]) => (
                      <span key={m} className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full capitalize">{m}: {fmt(amt)}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Breakdown for month/year (and hourly for day) */}
        {period !== 'day' && summary && (
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm mb-8">
            <div className="p-5 border-b border-stone-200 flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-stone-800">{periodTitle}</h2>
              {summary.top_items?.length > 0 && (
                <span className="text-xs text-stone-500">Top item: <b className="text-stone-700">{summary.top_items[0].name}</b> ({summary.top_items[0].qty})</span>
              )}
            </div>
            <div className="p-5 space-y-2" data-testid="sales-breakdown">
              {summary.breakdown.length === 0 && <div className="text-center py-8 text-stone-400">No sales in this period</div>}
              {summary.breakdown.map((b) => (
                <div key={b.label} className="flex items-center gap-3" data-testid={`breakdown-${b.label}`}>
                  <span className="w-16 text-sm text-stone-500 shrink-0">{label(b.label)}</span>
                  <div className="flex-1 bg-stone-100 rounded-full h-6 overflow-hidden">
                    <div className="h-full bg-secondary/70 rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(6, (b.sales / maxSales) * 100)}%` }}>
                      <span className="text-[11px] font-semibold text-stone-800">{fmt(b.sales)}</span>
                    </div>
                  </div>
                  <span className="w-20 text-right text-sm text-stone-500 shrink-0">{b.orders} order{b.orders === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invoice list for the day view */}
        {period === 'day' && (
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Invoice</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Payment</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-stone-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" data-testid={`sale-row-${o.order_id}`} onClick={() => setViewOrder(o)}>
                    <td className="px-4 py-3 font-mono text-sm text-primary">{o.invoice_no}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{new Date(o.created_at).toLocaleTimeString('en-IN')}</td>
                    <td className="px-4 py-3">{o.customer_name || '—'}</td>
                    <td className="px-4 py-3 capitalize text-sm">{o.payments.map((p) => p.mode).join(', ')}</td>
                    <td className="px-4 py-3 text-right font-semibold">{fmt(o.grand_total)}</td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-stone-400">No sales for this date</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!viewOrder} onOpenChange={(o) => !o && setViewOrder(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="no-print">Invoice {viewOrder?.invoice_no}</DialogTitle></DialogHeader>
          <Invoice order={viewOrder} business={business} />
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
